import { CircularProgress } from '@mui/material';
import UPButton from 'components/button';
import ErrorBar from 'components/error-bar';
import UPSidePanel, { UPSidePanelSection } from 'components/side-panel';
import UPSwitch from 'components/switch';
import { useTranslation } from 'react-i18next';
import {
  FEATURE_ACCESS__CONCURRENT_CHANGE,
  GENERAL__CLOSE,
  GENERAL__RETRY,
  GENERAL__SAVE_BUTTON,
  MANAGEMENT__FEATURE_ACCESS,
} from 'translations/constants';
import { FEATURE_LABEL_KEYS, FEATURES } from './constants';
import { UseFeatureAccessPanelResult } from './hooks/useFeatureAccessPanel';
import { PanelErrorRow, PanelFeatureRow } from './styles';

interface FeatureAccessPanelProps {
  panel: UseFeatureAccessPanelResult;
}

const FeatureAccessPanel = ({ panel }: FeatureAccessPanelProps): JSX.Element => {
  const [t] = useTranslation();
  const { status, draft, isSaving, loadErrorKey, saveErrorKey } = panel;

  return (
    <UPSidePanel
      open={status !== 'closed'}
      title={t(MANAGEMENT__FEATURE_ACCESS)}
      subtitle={panel.companyName}
      onClose={panel.close}
      testId="feature-access-panel"
      footer={
        <>
          <UPButton
            text={t(GENERAL__SAVE_BUTTON)}
            onClick={panel.save}
            disabled={status !== 'ready' || isSaving}
            testId="feature-access-save"
          />
          <UPButton text={t(GENERAL__CLOSE)} onClick={panel.close} outlined testId="feature-access-close" />
        </>
      }
    >
      {status === 'loading' && <CircularProgress />}

      {status === 'loadError' && (
        <PanelErrorRow>
          <ErrorBar message={t(loadErrorKey)} />
          <UPButton text={t(GENERAL__RETRY)} onClick={panel.retryLoad} outlined testId="feature-access-panel-retry" />
        </PanelErrorRow>
      )}

      {saveErrorKey && (
        <PanelErrorRow>
          <ErrorBar message={t(saveErrorKey)} />
          {saveErrorKey !== FEATURE_ACCESS__CONCURRENT_CHANGE && (
            <UPButton
              text={t(GENERAL__RETRY)}
              onClick={panel.save}
              disabled={isSaving || status !== 'ready'}
              outlined
              testId="feature-access-save-retry"
            />
          )}
        </PanelErrorRow>
      )}

      {status === 'ready' && draft && (
        // Titled white card with the company being edited, like the contract detail panel's sections.
        <UPSidePanelSection title={panel.companyName} testId="feature-access-details">
          {FEATURES.map(feature => (
            // No line per setting: the divider under the section title already separates them from the header.
            <PanelFeatureRow key={feature} sx={{ borderBottom: 'none' }}>
              <span>{t(FEATURE_LABEL_KEYS[feature])}</span>
              <UPSwitch
                checked={draft[feature]}
                onChange={() => panel.toggle(feature)}
                disabled={isSaving}
                testId={`feature-access-toggle-${feature}`}
              />
            </PanelFeatureRow>
          ))}
        </UPSidePanelSection>
      )}
    </UPSidePanel>
  );
};

export default FeatureAccessPanel;
